import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import PrivateLayout from '../components/PrivateLayout';
import PageHeader from '../components/PageHeader';
import api from '../services/api';
import { LangContext } from '../App';

const T = {
  fr: {
    title: 'Soumettre une recette',
    modifier: 'Modifier la recette',
    champsTitre: 'Titre de la recette',
    especePrincipale: 'Espèce principale',
    difficulty: 'Difficulté',
    prepTime: 'Temps de préparation (min)',
    cookTime: 'Temps de cuisson (min)',
    servings: 'Portions',
    ingredients: 'Ingrédients',
    ajouterIngredient: '+ Ajouter un ingrédient',
    steps: 'Étapes de préparation',
    ajouterEtape: '+ Ajouter une étape',
    description: 'Description (optionnelle)',
    envoyer: 'Soumettre pour révision',
    enregistrer: 'Enregistrer les modifications',
    annuler: 'Annuler',
    succes: 'Recette soumise avec succès !',
    erreur: 'Une erreur est survenue.',
    connexionRequise: 'Vous devez être connecté pour soumettre une recette.',
    nomIngredient: 'Nom',
    quantite: 'Quantité',
    unite: 'Unité',
    texteEtape: "Description de l'étape…",
    choisirEspece: 'Choisir une espèce…',
    choisirDifficulte: 'Choisir une difficulté…',
    retirerIngredient: '×',
    retirerEtape: '×',
    chargement: 'Chargement…',
    nonAutorise: "Vous n'êtes pas autorisé à modifier cette recette.",
    difficultes: [
      { v: 'facile', l: 'Facile' },
      { v: 'moyen', l: 'Moyen' },
      { v: 'difficile', l: 'Difficile' },
      { v: 'expert', l: 'Expert' },
    ],
    especes: [
      'orignal',
      'chevreuil',
      'caribou',
      'dinde_sauvage',
      'outarde',
      'canard',
      'bernache',
      'brochet',
      'dore',
      'achigan',
      'truite',
      'saumon',
      'autre',
    ],
    especesLabels: {
      orignal: 'Orignal',
      chevreuil: 'Chevreuil',
      caribou: 'Caribou',
      dinde_sauvage: 'Dinde sauvage',
      outarde: 'Outarde',
      canard: 'Canard',
      bernache: 'Bernache',
      brochet: 'Brochet',
      dore: 'Doré',
      achigan: 'Achigan',
      truite: 'Truite',
      saumon: 'Saumon',
      autre: 'Autre',
    },
  },
  en: {
    title: 'Submit a recipe',
    modifier: 'Edit recipe',
    champsTitre: 'Recipe title',
    especePrincipale: 'Main species',
    difficulty: 'Difficulty',
    prepTime: 'Prep time (min)',
    cookTime: 'Cook time (min)',
    servings: 'Servings',
    ingredients: 'Ingredients',
    ajouterIngredient: '+ Add ingredient',
    steps: 'Steps',
    ajouterEtape: '+ Add step',
    description: 'Description (optional)',
    envoyer: 'Submit for review',
    enregistrer: 'Save changes',
    annuler: 'Cancel',
    succes: 'Recipe submitted successfully!',
    erreur: 'An error occurred.',
    connexionRequise: 'You must be logged in to submit a recipe.',
    nomIngredient: 'Name',
    quantite: 'Quantity',
    unite: 'Unit',
    texteEtape: 'Step description…',
    choisirEspece: 'Choose a species…',
    choisirDifficulte: 'Choose a difficulty…',
    retirerIngredient: '×',
    retirerEtape: '×',
    chargement: 'Loading…',
    nonAutorise: 'You are not authorized to edit this recipe.',
    difficultes: [
      { v: 'facile', l: 'Easy' },
      { v: 'moyen', l: 'Medium' },
      { v: 'difficile', l: 'Hard' },
      { v: 'expert', l: 'Expert' },
    ],
    especes: [
      'orignal',
      'chevreuil',
      'caribou',
      'dinde_sauvage',
      'outarde',
      'canard',
      'bernache',
      'brochet',
      'dore',
      'achigan',
      'truite',
      'saumon',
      'autre',
    ],
    especesLabels: {
      orignal: 'Moose',
      chevreuil: 'Deer',
      caribou: 'Caribou',
      dinde_sauvage: 'Wild Turkey',
      outarde: 'Canada Goose',
      canard: 'Duck',
      bernache: 'Brant',
      brochet: 'Pike',
      dore: 'Walleye',
      achigan: 'Bass',
      truite: 'Trout',
      saumon: 'Salmon',
      autre: 'Other',
    },
  },
};

const emptyIngredient = () => ({ name: '', quantite: '', unite: '' });
const emptyStep = () => ({ texte: '' });

export default function SoumettreRecette() {
  const { id } = useParams();
  const isEditing = Boolean(id);
  const { lang } = useContext(LangContext);
  const t = T[lang] || T.fr;
  const navigate = useNavigate();

  const token = localStorage.getItem('token');

  const [title, setTitle] = useState('');
  const [species, setSpecies] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [prepTime, setPrepTime] = useState('');
  const [cookTime, setCookTime] = useState('');
  const [servings, setServings] = useState('');
  const [description, setDescription] = useState('');
  const [ingredients, setIngredients] = useState([emptyIngredient()]);
  const [steps, setSteps] = useState([emptyStep()]);

  const [submitting, setSubmitting] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(isEditing);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!isEditing) return;
    const fetchForEdit = async () => {
      setLoadingEdit(true);
      try {
        const { data } = await api.get(`/recipes/${id}`);
        setTitle(data.title || '');
        setSpecies(data.main_species || '');
        setDifficulty(data.difficulty || '');
        setPrepTime(data.prep_time_min || '');
        setCookTime(data.cook_time_min || '');
        setServings(data.servings || '');
        setDescription(data.description || '');
        setIngredients(data.ingredients?.length ? data.ingredients : [emptyIngredient()]);
        setSteps(data.steps?.length ? data.steps : [emptyStep()]);
      } catch {
        setErrorMessage(t.erreur);
      } finally {
        setLoadingEdit(false);
      }
    };
    fetchForEdit();
  }, [id, isEditing]);

  // Ingredient handlers
  const handleIngredientChange = (index, field, value) => {
    setIngredients((prev) =>
      prev.map((ing, i) => (i === index ? { ...ing, [field]: value } : ing))
    );
  };
  const addIngredient = () => setIngredients((prev) => [...prev, emptyIngredient()]);
  const removeIngredient = (index) => setIngredients((prev) => prev.filter((_, i) => i !== index));

  // Step handlers
  const handleStepChange = (index, value) => {
    setSteps((prev) => prev.map((step, i) => (i === index ? { texte: value } : step)));
  };
  const addStep = () => setSteps((prev) => [...prev, emptyStep()]);
  const removeStep = (index) => setSteps((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      setErrorMessage(t.connexionRequise);
      return;
    }
    setSubmitting(true);
    setSuccessMessage('');
    setErrorMessage('');

    const payload = {
      title: title,
      main_species: species,
      difficulty: difficulty,
      prep_time_min: prepTime ? parseInt(prepTime, 10) : undefined,
      cook_time_min: cookTime ? parseInt(cookTime, 10) : undefined,
      servings: servings ? parseInt(servings, 10) : undefined,
      description,
      ingredients: ingredients.filter((ing) => ing.name.trim()),
      steps: steps.filter((step) => step.texte.trim()),
    };

    try {
      if (isEditing) {
        await api.put(`/recipes/${id}`, payload);
        setSuccessMessage(t.succes);
        setTimeout(() => navigate(`/recipes/${id}`), 1200);
      } else {
        const { data } = await api.post('/recipes', payload);
        setSuccessMessage(t.succes);
        const newId = data?.id || data?.recette?.id;
        setTimeout(() => navigate(newId ? `/recipes/${newId}` : '/recipes'), 1200);
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setErrorMessage(t.nonAutorise);
      } else {
        setErrorMessage(t.erreur);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const s = {
    page: { minHeight: '100vh', background: 'var(--couleur-fond)', color: 'var(--couleur-texte)' },
    hero: {
      background: 'linear-gradient(135deg, #0a1f0e 0%, #1f3e1f 100%)',
      padding: '2.5rem 2rem',
      textAlign: 'center',
    },
    heroTitle: {
      fontFamily: 'Georgia, serif',
      fontSize: 'clamp(1.6rem, 3.5vw, 2.2rem)',
      color: '#fff',
      margin: 0,
    },
    formWrapper: { maxWidth: 880, margin: '0 auto', paddingBottom: '4rem' },
    card: {
      background: 'var(--couleur-fond-carte)',
      borderRadius: '14px',
      border: '1px solid rgba(255,255,255,0.08)',
      padding: '2rem',
    },
    sectionTitle: {
      fontFamily: 'Georgia, serif',
      fontSize: '1.05rem',
      color: 'var(--couleur-or)',
      margin: '1.5rem 0 0.75rem',
      borderBottom: '1px solid rgba(255,255,255,0.07)',
      paddingBottom: '0.4rem',
    },
    label: {
      display: 'block',
      fontSize: '0.82rem',
      color: 'rgba(255,255,255,0.55)',
      marginBottom: '5px',
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      boxSizing: 'border-box',
      background: 'rgba(255,255,255,0.07)',
      border: '1px solid rgba(255,255,255,0.15)',
      color: '#fff',
      fontSize: '0.92rem',
      outline: 'none',
    },
    select: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      boxSizing: 'border-box',
      background: 'rgba(255,255,255,0.07)',
      border: '1px solid rgba(255,255,255,0.15)',
      color: '#fff',
      fontSize: '0.92rem',
      cursor: 'pointer',
      outline: 'none',
    },
    textarea: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      boxSizing: 'border-box',
      background: 'rgba(255,255,255,0.07)',
      border: '1px solid rgba(255,255,255,0.15)',
      color: '#fff',
      fontSize: '0.92rem',
      resize: 'vertical',
      outline: 'none',
      minHeight: '80px',
    },
    fieldGroup: { marginBottom: '1rem' },
    twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' },
    threeCol: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr',
      gap: '1rem',
      marginBottom: '1rem',
    },
    ingredientRow: {
      display: 'grid',
      gridTemplateColumns: '2fr 1fr 1fr auto',
      gap: '8px',
      alignItems: 'end',
      marginBottom: '8px',
    },
    stepRow: { display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '8px' },
    stepNum: {
      width: 28,
      height: 28,
      borderRadius: '50%',
      background: 'rgba(201,162,39,0.2)',
      border: '1px solid rgba(201,162,39,0.4)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.8rem',
      fontWeight: 700,
      color: 'var(--couleur-or)',
      flexShrink: 0,
      marginTop: '8px',
    },
    btnRemove: {
      padding: '0 10px',
      height: '36px',
      borderRadius: '6px',
      border: '1px solid rgba(251,113,133,0.3)',
      background: 'rgba(251,113,133,0.1)',
      color: '#fb7185',
      cursor: 'pointer',
      fontSize: '1rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    btnAdd: {
      padding: '9px 16px',
      borderRadius: '8px',
      border: '1px dashed rgba(255,255,255,0.25)',
      background: 'transparent',
      color: 'rgba(255,255,255,0.6)',
      cursor: 'pointer',
      fontSize: '0.88rem',
      marginTop: '4px',
      width: '100%',
      transition: 'all 0.15s',
    },
    btnSubmit: {
      width: '100%',
      padding: '13px',
      borderRadius: '10px',
      border: 'none',
      background: 'var(--couleur-vert-accent)',
      color: '#fff',
      fontWeight: 700,
      fontSize: '1rem',
      cursor: 'pointer',
      marginTop: '1.5rem',
      transition: 'opacity 0.15s',
    },
    btnCancel: {
      display: 'block',
      textAlign: 'center',
      marginTop: '0.75rem',
      color: 'rgba(255,255,255,0.45)',
      fontSize: '0.88rem',
      textDecoration: 'none',
    },
    successMsg: {
      padding: '12px 16px',
      borderRadius: '8px',
      background: 'rgba(52,211,153,0.15)',
      border: '1px solid rgba(52,211,153,0.3)',
      color: '#34d399',
      fontSize: '0.92rem',
      marginBottom: '1rem',
    },
    errorMsg: {
      padding: '12px 16px',
      borderRadius: '8px',
      background: 'rgba(251,113,133,0.12)',
      border: '1px solid rgba(251,113,133,0.3)',
      color: '#fb7185',
      fontSize: '0.92rem',
      marginBottom: '1rem',
    },
  };

  if (!token) {
    return (
      <PrivateLayout>
        <div style={{ textAlign: 'center', padding: '5rem 2rem' }}>
          <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '1rem' }}>
            {t.connexionRequise}
          </p>
          <Link to="/login" style={{ color: 'var(--couleur-or)', textDecoration: 'none' }}>
            Connexion / Login
          </Link>
        </div>
      </PrivateLayout>
    );
  }

  if (loadingEdit) {
    return (
      <PrivateLayout>
        <div style={{ textAlign: 'center', padding: '5rem 2rem', color: 'rgba(255,255,255,0.4)' }}>
          {t.chargement}
        </div>
      </PrivateLayout>
    );
  }

  return (
    <PrivateLayout>
      <div style={s.formWrapper}>
        <PageHeader icon="📖" title={isEditing ? t.modifier : t.title} />
        <form style={s.card} onSubmit={handleSubmit}>
          {successMessage && <div style={s.successMsg}>{successMessage}</div>}
          {errorMessage && <div style={s.errorMsg}>{errorMessage}</div>}

          {/* Title */}
          <div style={s.fieldGroup}>
            <label style={s.label}>{t.champsTitre} *</label>
            <input
              style={s.input}
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          {/* Species + Difficulty */}
          <div style={s.twoCol}>
            <div>
              <label style={s.label}>{t.especePrincipale}</label>
              <select style={s.select} value={species} onChange={(e) => setSpecies(e.target.value)}>
                <option value="">{t.choisirEspece}</option>
                {t.especes.map((esp) => (
                  <option key={esp} value={esp}>
                    {t.especesLabels[esp]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={s.label}>{t.difficulty}</label>
              <select
                style={s.select}
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
              >
                <option value="">{t.choisirDifficulte}</option>
                {t.difficultes.map((d) => (
                  <option key={d.v} value={d.v}>
                    {d.l}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Timing + servings */}
          <div style={s.threeCol}>
            <div>
              <label style={s.label}>{t.prepTime}</label>
              <input
                style={s.input}
                type="number"
                min="0"
                value={prepTime}
                onChange={(e) => setPrepTime(e.target.value)}
              />
            </div>
            <div>
              <label style={s.label}>{t.cookTime}</label>
              <input
                style={s.input}
                type="number"
                min="0"
                value={cookTime}
                onChange={(e) => setCookTime(e.target.value)}
              />
            </div>
            <div>
              <label style={s.label}>{t.servings}</label>
              <input
                style={s.input}
                type="number"
                min="1"
                value={servings}
                onChange={(e) => setServings(e.target.value)}
              />
            </div>
          </div>

          {/* Description */}
          <div style={s.fieldGroup}>
            <label style={s.label}>{t.description}</label>
            <textarea
              style={s.textarea}
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Ingredients */}
          <h3 style={s.sectionTitle}>{t.ingredients}</h3>
          {ingredients.map((ing, i) => (
            <div key={i} style={s.ingredientRow}>
              <div>
                {i === 0 && <label style={s.label}>{t.nomIngredient}</label>}
                <input
                  style={s.input}
                  type="text"
                  value={ing.name}
                  onChange={(e) => handleIngredientChange(i, 'name', e.target.value)}
                  placeholder={t.nomIngredient}
                />
              </div>
              <div>
                {i === 0 && <label style={s.label}>{t.quantite}</label>}
                <input
                  style={s.input}
                  type="text"
                  value={ing.quantite}
                  onChange={(e) => handleIngredientChange(i, 'quantite', e.target.value)}
                  placeholder={t.quantite}
                />
              </div>
              <div>
                {i === 0 && <label style={s.label}>{t.unite}</label>}
                <input
                  style={s.input}
                  type="text"
                  value={ing.unite}
                  onChange={(e) => handleIngredientChange(i, 'unite', e.target.value)}
                  placeholder={t.unite}
                />
              </div>
              <button
                type="button"
                style={{ ...s.btnRemove, marginTop: i === 0 ? '18px' : '0' }}
                onClick={() => removeIngredient(i)}
                disabled={ingredients.length === 1}
              >
                {t.retirerIngredient}
              </button>
            </div>
          ))}
          <button type="button" style={s.btnAdd} onClick={addIngredient}>
            {t.ajouterIngredient}
          </button>

          {/* Steps */}
          <h3 style={s.sectionTitle}>{t.steps}</h3>
          {steps.map((step, i) => (
            <div key={i} style={s.stepRow}>
              <div style={s.stepNum}>{i + 1}</div>
              <textarea
                style={{ ...s.textarea, flex: 1 }}
                rows={2}
                value={step.texte}
                onChange={(e) => handleStepChange(i, e.target.value)}
                placeholder={t.texteEtape}
              />
              <button
                type="button"
                style={{ ...s.btnRemove, marginTop: '4px' }}
                onClick={() => removeStep(i)}
                disabled={steps.length === 1}
              >
                {t.retirerEtape}
              </button>
            </div>
          ))}
          <button type="button" style={s.btnAdd} onClick={addStep}>
            {t.ajouterEtape}
          </button>

          {/* Submit */}
          <button
            type="submit"
            style={{ ...s.btnSubmit, opacity: submitting ? 0.65 : 1 }}
            disabled={submitting || !title.trim()}
          >
            {submitting ? '…' : isEditing ? t.enregistrer : t.envoyer}
          </button>

          <Link to={isEditing ? `/recipes/${id}` : '/recipes'} style={s.btnCancel}>
            {t.annuler}
          </Link>
        </form>
      </div>
    </PrivateLayout>
  );
}
